import { VulnerabilitiesAppView } from '../view/VulnerabilitiesAppView';
import { templateVulnerabilitiesApp } from '../view/template';
import { VulnerabilitiesAppRepository } from '../repository/VulnerabilitiesAppRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { VulnerabilitiesAppWebComponent } from '..';
import { ColumnKeys, VulnerabilitiesAppWebComponentSchema } from '../types';
import {
  getSideNavigationPanelSmartViewsListener,
  getSmartViewsListeners,
} from '../../../grid/genericGridSmartViews/utils';
import { GridEvents } from '../../../grid/types/common';
import { VulnerabilitiesSideMenuWebComponent } from '../../sideMenus/vulnerabilitiesSideMenu';
import {
  applyDetailsToShowExtraData,
  applyDeviceDetailsChangedEventOnSideComponent,
  applyDeviceExtraDetailsChangedEventOnSideComponent,
  buildPatchAppAction,
  createActionDialog,
  getEndpointIdsFromAffectedDevices,
  renderDialogText, renderRiskScanOptions,
  RiskManagementSideMenuEvents,
} from '../../sideMenus/common/common';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import {
  buildCategoriesSideMenu,
  buildPatchManagementInformation,
  handleChangeStateBulkSelectionChanged,
  handleChangeWatchlistBulkSelectionChanged,
  handleComplexEnumFilter,
  handlePivotFromExtraDetailsPanelToAdvancedSearch,
  handlePivotFromExtraDetailsPanelToIncidents,
  handleRowsLoadingFinishedEvent,
  handleScan,
  handleSelectionChange,
  overlayOpenListener,
  processAffectedDeviceData,
  updateWatchlist,
} from '../../common/utils';
import {
  Actions,
  ActionsEvents, CSV_TYPE,
  DetailsType,
  FULFILLED_PROMISE_STATUS,
  InlineMenuExtraDetailsSelectEvents,
  SelectionEvents,
} from '../../common/constants';
import {
  WEB_COMPONENT_USERS_SIDE_MENU,
  WEB_COMPONENT_VULNERABILITIES_SIDE_MENU,
} from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';
import { ButtonColors, ButtonEvents, GzButtonState } from '../../../ui/gzButton/types';
import { SelectEvents } from '../../../ui/gzSelect/types';
import {
  DeviceDetailsChangedEvent,
  DeviceExtraDetailsChangedEvent,
  ShowExtraDataEventDetails,
} from '../../sideMenus/components/deviceDetails/types/types';
import { ContextMenuOverlayEvents } from '../../../ui/gzContextMenuOverlay/component/gzContextMenuOverlay';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { removeActiveStateFromGzIconFromAffectedDevices } from '../../common/customRenderers';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzRadioGroupWebComponent } from '../../../ui/gzRadio';
import { html } from 'lit-html';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { ExtJsFilter } from '../../../grid/utils/filters/types';
import { showToast } from '../../../tagsManager/helpers/commonFunctions';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';

export class VulnerabilitiesAppController extends BaseWebComponentController {
  public component: VulnerabilitiesAppWebComponent;
  public dataRepository: VulnerabilitiesAppRepository;
  public view: VulnerabilitiesAppView;
  public viewConstructor: typeof VulnerabilitiesAppView;
  public dataRepositoryConstructor: typeof VulnerabilitiesAppRepository;

  selectedItems: Array<any>;

  constructor(component: VulnerabilitiesAppWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: VulnerabilitiesAppView,
      viewTemplate: templateVulnerabilitiesApp,
      dataRepositoryConstructor: VulnerabilitiesAppRepository,
    });
  }

  public computeEventListeners() {
    return [
      ...getSmartViewsListeners(
        this.view.dom.$grid,
        this.view.dom.$gridSmartViews,
        this.view.buildGrid.bind(this.view),
        this.view.getTranslations(),
      ),
      ...getSideNavigationPanelSmartViewsListener(this.view.dom.$pushBodyLayout, this.view.dom.$gridSmartViews),
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: handleRowsLoadingFinishedEvent.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.IGNORE_APPLICATIONS,
        callback: this.handleIgnoreApplicationsEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.RESTORE_APPLICATIONS,
        callback: this.handleRestoreApplicationsEvent,
      },
      {
        element: this.view.dom.$grid,
        event: RiskManagementSideMenuEvents.IGNORE_APPLICATIONS,
        callback: this.handleIgnoreApplicationsEvent,
      },
      {
        element: this.view.dom.$grid,
        event: RiskManagementSideMenuEvents.RESTORE_APPLICATIONS,
        callback: this.handleRestoreApplicationsEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.PATCH_APP,
        callback: this.handlePatchAppEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$grid,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.DEVICE_DETAILS_CHANGED,
        callback: this.handleDeviceDetailsChangedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.DEVICE_DETAILS_SHOW_EXTRA_DATA,
        callback: this.handleDeviceDetailsShowExtraDataEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.DEVICE_EXTRA_DETAILS_CHANGED,
        callback: this.handleDeviceExtraDetailsChangedEvent,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EXPORT_CSV,
        callback: this.handleCSVExportEvent,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: handleSelectionChange.bind(this),
      },
      {
        element: this.view.dom.changeStateBulk.$select,
        event: SelectEvents.SELECTION_CHANGED,
        callback: handleChangeStateBulkSelectionChanged.bind(this),
      },
      {
        element: this.view.dom.changeWatchlistBulk.$select,
        event: SelectEvents.SELECTION_CHANGED,
        callback: handleChangeWatchlistBulkSelectionChanged.bind(this),
      },
      {
        element: this.view.dom.$scanEmptyStateButton,
        event: ButtonEvents.CLICK,
        callback: handleScan.bind(this),
      },
      {
        element: this.view.dom.scanButton.$button,
        event: ButtonEvents.CLICK,
        callback: handleScan.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: InlineMenuExtraDetailsSelectEvents.viewIncidents,
        callback: handlePivotFromExtraDetailsPanelToIncidents.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: InlineMenuExtraDetailsSelectEvents.viewEvents,
        callback: handlePivotFromExtraDetailsPanelToAdvancedSearch.bind(this),
      },
      {
        // This was needed in order to modify the pointer-events attribute on body
        // It was being set on none at overlay opening and was preventing
        // automated tests to click on other elements while the overlay was open
        element: this.view.dom.$overlay,
        event: ContextMenuOverlayEvents.OPEN,
        callback: overlayOpenListener,
      },
      {
        element: this.view.dom.$grid,
        event: SelectionEvents.RESOURCE_ICON_CLICK,
        callback: this.handleAffectedResourceIconClickedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: SideMenuEvents.CLOSE,
        callback: this.handleRightSideNavClosed,
      },
    ];
  }

  async getDataForView(): Promise<VulnerabilitiesAppWebComponentSchema> {
    return {
      data: await this.dataRepository.getDataForView(),
      externalFilters: this.component.externalFilters,
    };
  }

  async handleRowClicked(event: CustomEvent) {
    if (!event.detail.rowDetails) {
      // header was clicked, so we do nothing
      return;
    }

    const component = new VulnerabilitiesSideMenuWebComponent();
    component.item = event.detail.rowDetails;
    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();

    let cveListResponse: null | any = null;
    let patchDataResponseArray: null | any = null;
    let deviceDetailsDataResponse: null | any = null;

    const cveListResponsePromise = ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'GlobalInventoryCve',
      method: 'getCVEsForApplication',
      payload: {
        applicationId: event.detail.rowDetails._id,
      },
    });

    const hasPatchManagement = event.detail.rowDetails.company?.specifics?.features?.patchManagementUsable ?? false;
    const isCompanionStandalone = event.detail.rowDetails.company?.specifics?.features?.companionStandalone ?? false;

    const patchDataResponsePromise = hasPatchManagement
      ? ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'Vulnerabilities',
        method: 'getPatchManagementInformation',
        payload: {
          applicationIds: [event.detail.rowDetails._id],
          companyId: event.detail.rowDetails.company._id,
        },
      })
      : [{ hasPatchManagement: false }];

    const { firstEndpointIdFromAffectedDevice, allEndpointsIds } = getEndpointIdsFromAffectedDevices(
      event.detail.rowDetails.affectedDevices,
    );

    let cveListResponseResult, patchDataResponseArrayResult, deviceDataResponseArrayResult;

    if (firstEndpointIdFromAffectedDevice) {
      const deviceDataResponsePromise = ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'RiskManagementDevices',
        method: 'getExtraDetailsForDevice',
        payload: {
          companyId: event.detail.rowDetails.company._id,
          endpointId: firstEndpointIdFromAffectedDevice,
          type: DetailsType.VULNERABILITIES,
          uniqueIdentifierForFinding: event.detail.rowDetails.uniqueIdentifier,
        },
      });

      [cveListResponseResult, patchDataResponseArrayResult, deviceDataResponseArrayResult] = await Promise.allSettled([
        cveListResponsePromise,
        patchDataResponsePromise,
        deviceDataResponsePromise,
      ]);
    } else {
      [cveListResponseResult, patchDataResponseArrayResult] = await Promise.allSettled([
        cveListResponsePromise,
        patchDataResponsePromise,
      ]);
    }

    if (cveListResponseResult.status === FULFILLED_PROMISE_STATUS) {
      cveListResponse = cveListResponseResult.value;
    }

    if (patchDataResponseArrayResult.status === FULFILLED_PROMISE_STATUS) {
      patchDataResponseArray = patchDataResponseArrayResult.value;
    }

    if (deviceDataResponseArrayResult && deviceDataResponseArrayResult.status === FULFILLED_PROMISE_STATUS) {
      deviceDetailsDataResponse = deviceDataResponseArrayResult.value;
    }

    const allCves = event.detail.rowDetails.cves;

    const cveList = cveListResponse ?? [];
    const cvesFromResponse: string[] = cveList.map((cve: { standardId: string }) => cve.standardId);
    for (const cve of allCves) {
      if (cvesFromResponse.indexOf(cve) === -1) {
        cveList.push({
          standardId: cve,
          hasMissingDetails: true,
        });
      }
    }
    component.cveList = cveList;

    if (patchDataResponseArray && Array.isArray(patchDataResponseArray) && patchDataResponseArray.length > 0) {
      const [patchDataResponse] = patchDataResponseArray;

      component.patchManagementDetails = buildPatchManagementInformation(
        event.detail.rowDetails._id,
        patchDataResponse,
        isCompanionStandalone,
      );
    }

    this.enhanceSideMenuWithDeviceDetailsData(
      component,
      deviceDetailsDataResponse,
      firstEndpointIdFromAffectedDevice,
      allEndpointsIds,
      event,
    );
  }

  enhanceSideMenuWithDeviceDetailsData(
    component,
    deviceDetailsDataResponse,
    firstEndpointIdFromAffectedDevice,
    allEndpointsIds,
    event,
  ) {
    if (
      firstEndpointIdFromAffectedDevice &&
      deviceDetailsDataResponse &&
      Object.keys(deviceDetailsDataResponse).length > 0
    ) {
      component.deviceInitialDropdownDetails = {
        endpointId: deviceDetailsDataResponse['device']['id'],
        endpointName: deviceDetailsDataResponse['device']['name'],
        companyId: event.detail.rowDetails.company._id,
        endpointIds: allEndpointsIds,
        uniqueIdentifierForFinding: event.detail.rowDetails.uniqueIdentifier,
      };

      component.initialDeviceDetails = deviceDetailsDataResponse['specifics'];
      component.deviceDetails = deviceDetailsDataResponse['specifics'];
    } else if (!firstEndpointIdFromAffectedDevice) {
      component.deviceDetailsEmptyState = true;
    } else {
      component.deviceDetailsHasErrors = true;
    }
  }

  private handleIgnoreApplicationsEvent(event: CustomEvent) {
    const { applicationsIds, companyId } = event.detail;

    this.createIgnoreDialog(applicationsIds, companyId);
  }

  private createIgnoreDialog(applicationsIds, companyId) {
    let data;

    if (applicationsIds.length === 1) {
      data = {
        dialogBodyMessage: `
          ${renderDialogText(
          translateFn(
            'The selected application will not be included in the overall resources and company risk score calculation. You can revert this action at any time.',
          ),
        )}
        `,
        dialogTitle: translateFn('Ignore application?'),
        confirmButtonText: translateFn('Ignore application'),
        toastMessages: {
          success: translateFn('The application has been ignored. Changes will apply shortly.'),
          error: translateFn('Failed to ignore application.'),
        },
      };
    } else {
      data = {
        dialogBodyMessage: `
      ${renderDialogText(
          translateFn(
            'The selected applications will not be included in the overall resources and company risk score calculation. You can revert this action at any time.',
          ),
        )}
    `,
        dialogTitle: translateFn('Ignore applications?'),
        confirmButtonText: translateFn('Ignore applications'),
        toastMessages: {
          success: translateFn('The applications have been ignored. Changes will apply shortly.'),
          error: translateFn('Failed to ignore applications.'),
        },
      };
    }

    const requestPayload = {
      parameters: {
        actionType: Actions.IGNORE_APPLICATIONS,
        applicationsIds,
        companyId,
      },
    };
    createActionDialog(
      {
        dialogTitle: data.dialogTitle,
        confirmButtonText: data.confirmButtonText,
        dialogBodyMessage: data.dialogBodyMessage,
      },
      {
        grid: this.view.dom.$grid,
        sideMenu: this.view.dom.$sideMenu,
      },
      requestPayload,
      data.toastMessages,
    );
  }

  private createRestoreDialog(applicationsIds, companyId) {
    let data;

    if (applicationsIds.length === 1) {
      data = {
        dialogBodyMessage: `
          ${renderDialogText(
          translateFn(
            'The selected application will now be included in the overall resources and company risk score calculation. You can revert this action at any time.',
          ),
        )}
        `,
        dialogTitle: translateFn('Restore ignored application?'),
        confirmButtonText: translateFn('Restore ignored application'),
        toastMessages: {
          success: translateFn('The application has been restored. Changes will apply shortly.'),
          error: translateFn('Failed to restore application.'),
        },
      };
    } else {
      data = {
        dialogBodyMessage: `
          ${renderDialogText(
          translateFn(
            'The selected applications will now be included in the overall resources and company risk score calculation. You can revert this action at any time.',
          ),
        )}
        `,
        dialogTitle: translateFn('Restore ignored applications?'),
        confirmButtonText: translateFn('Restore ignored applications'),
        toastMessages: {
          success: translateFn('Ignored applications have been restored. Changes will apply shortly.'),
          error: translateFn('Failed to restore applications.'),
        },
      };
    }

    const requestPayload = {
      parameters: {
        actionType: Actions.RESTORE_APPLICATIONS,
        applicationsIds,
        companyId,
      },
    };
    createActionDialog(
      {
        dialogBodyMessage: data.dialogBodyMessage,
        dialogTitle: data.dialogTitle,
        confirmButtonText: data.confirmButtonText,
      },
      {
        grid: this.view.dom.$grid,
        sideMenu: this.view.dom.$sideMenu,
      },
      requestPayload,
      data.toastMessages,
    );
  }

  private handleRestoreApplicationsEvent(event: CustomEvent) {
    const { applicationsIds, companyId } = event.detail;

    this.createRestoreDialog(applicationsIds, companyId);
  }

  private handlePatchAppEvent(event: CustomEvent) {
    buildPatchAppAction(event, this.view.dom.$grid, this.view.dom.$sideMenu);
  }

  private async handleWatchlistChangedEvent(event: CustomEvent) {
    const { newWatchlist, ids, companyId } = event.detail;
    await this.updateWatchlistStatus(newWatchlist, ids, companyId);
  }

  private async handleDeviceDetailsChangedEvent(event: CustomEvent) {
    const sideMenuComponent = this.view.dom.$sideMenu.querySelector(
      WEB_COMPONENT_VULNERABILITIES_SIDE_MENU,
    ) as VulnerabilitiesSideMenuWebComponent;

    await applyDeviceDetailsChangedEventOnSideComponent(
      sideMenuComponent,
      DetailsType.VULNERABILITIES,
      event.detail as DeviceDetailsChangedEvent,
    );
  }

  private async handleDeviceExtraDetailsChangedEvent(event: CustomEvent) {
    const sideMenuComponent = this.view.dom.$sideMenu.querySelector(
      WEB_COMPONENT_VULNERABILITIES_SIDE_MENU,
    ) as VulnerabilitiesSideMenuWebComponent;

    await applyDeviceExtraDetailsChangedEventOnSideComponent(
      sideMenuComponent,
      DetailsType.VULNERABILITIES,
      event.detail as DeviceExtraDetailsChangedEvent,
    );
  }

  private async handleDeviceDetailsShowExtraDataEvent(event: CustomEvent) {
    const sideMenuComponent = this.view.dom.$sideMenu.querySelector(
      WEB_COMPONENT_VULNERABILITIES_SIDE_MENU,
    ) as VulnerabilitiesSideMenuWebComponent;

    applyDetailsToShowExtraData(sideMenuComponent, event.detail as ShowExtraDataEventDetails);
  }

  private handleCSVExportEvent(event: CustomEvent) {
    // if you change something here, change in GridDataProvider.getSummary also
    let filters: Array<ExtJsFilter<string>> = convertGridFiltersToExtJsFilters(event.detail.filters);
    const complexEnumFilterColumns = [ColumnKeys.NAME, ColumnKeys.CVES, ColumnKeys.AFFECTED_DEVICES];
    filters = handleComplexEnumFilter(complexEnumFilterColumns, filters);

    this.createCSVOptionsDialog(filters);
    return;
  }

  private async updateWatchlistStatus(newWatchlistStatus: number, indicatorsIds: Array<number>, companyId: string) {
    await updateWatchlist(
      {
        ids: indicatorsIds,
        companyId,
        newWatchlist: newWatchlistStatus,
      },
      this.view.dom.$grid,
      this.view.dom.$sideMenu,
      WEB_COMPONENT_USERS_SIDE_MENU,
      'Vulnerabilities',
    );
  }

  private async handleAffectedResourceIconClickedEvent(event: CustomEvent) {
    const { iconType, companyId, affectedResourcesIds } = event.detail;

    const affectedResourcesSideMenu = buildCategoriesSideMenu(companyId, iconType);

    this.view.dom.$sideMenu.setMenuContent(affectedResourcesSideMenu);
    this.view.dom.$sideMenu.openSideMenu();

    let typeToItemsMapping = [];
    try {
      typeToItemsMapping = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'Vulnerabilities',
        method: 'getAffectedResourcesDetails',
        payload: {
          companyId,
          affectedResourcesIds,
        },
      });
    } catch (error) {
      affectedResourcesSideMenu.hasErrors = true;
    }

    affectedResourcesSideMenu.initialCategories = processAffectedDeviceData(typeToItemsMapping);
  }

  handleRightSideNavClosed() {
    removeActiveStateFromGzIconFromAffectedDevices(this.view);
  }

  createCSVOptionsDialog(filters: Array<ExtJsFilter<string>>) {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `
      <div>
      <div class="mb-2">
        ${renderDialogText(translateFn('Select the representation of the application risk data') + ":")}
      </div>
        <div>
          <gz-radio-group gz-form-input selectedOption="${CSV_TYPE.NORMAL}" >
            <gz-radio value="${CSV_TYPE.NORMAL}" label="${translateFn('Overview')}"></gz-radio>
            <gz-radio value="${CSV_TYPE.BREAKDOWN}" label="${translateFn('Breakdown by resource')}"></gz-radio>
          </gz-radio-group>
        </div>
      </div>
    `;

    const dialogFooter = document.createElement('div');

    const confirmButton = new GzButtonWebComponent();
    confirmButton.setAttribute('color', ButtonColors.PRIMARY);
    confirmButton.textContent = translateFn('Export');

    const cancelButton = new GzButtonWebComponent();
    cancelButton.setAttribute('type', 'outline');
    cancelButton.setAttribute('color', ButtonColors.SECONDARY);
    cancelButton.textContent = translateFn('Cancel');

    dialogFooter.appendChild(confirmButton);
    dialogFooter.appendChild(cancelButton);

    const dialogTitle = translateFn('Export view as CSV');

    const dialogConfigs: GzDialog = {
      headline: dialogTitle,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    confirmButton?.addEventListener('click', async () => {
      const selectedValue = (dialogBody.querySelector('gz-radio-group') as GzRadioGroupWebComponent).getFormValue();

      const method = selectedValue === CSV_TYPE.BREAKDOWN ? 'exportBreakdownCSV' : 'exportItemsAsCsv';
      const url = app.buildDownloadUrl('Vulnerabilities', method, null, 'CSV');

      dialog.close();
      await Application.lib.DownloadManager.download(url, { filters }, true);


      showToast(ToastSeverity.SUCCESS, translateFn('The download will begin in a few moments.'));
    });

    cancelButton?.addEventListener('click', () => {
      dialog.close();
    });
  }
}
